import { PublicIdSchemas } from '@idc-forms/id'
import { IDENTIFICATION_CARDS } from '@idc-forms/types/identifications'
import { z } from 'zod'
import { InstantSchema, LocalDateSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de LECTURA del detalle de una negociación.
 *
 * Reemplaza a `GET /negotiations/:id`, que no tenía contrato: devolvía
 * `toObject()` del documento entero menos `history`, `deleted` e `id`. Todo lo
 * que Mongo guardara viajaba al navegador, incluido lo que ninguna pantalla lee
 * y lo que se agregara mañana sin que nadie lo decidiera.
 *
 * TRES COSAS QUE ESTE ARCHIVO CAMBIA respecto del legacy, y por qué:
 *
 * 1. **Se direcciona por `neg_`.** El legacy pedía el `_id` de Mongo; el ADR 0008
 *    prohíbe exponerlo. El campo ya existía en el schema con su índice parcial.
 *
 * 2. **Las referencias son `{ id, … }` con id PÚBLICO**, no documentos poblados.
 *    El legacy hacía once `populate` y devolvía lo que cada uno trajera. Acá cada
 *    referencia publica su id público y los pocos campos que la pantalla muestra
 *    —el nombre y el código—, que es lo que hace falta para pintar un chip sin
 *    pedir otra vuelta.
 *
 * 3. **Los propietarios traen su `cli_`.** Es lo que deja abrir el expediente
 *    FEIC desde la negociación: es el único id que aceptan los endpoints v2 de
 *    clientes. El legacy resolvía cada propietario con una consulta suelta
 *    (`getClientBasicInfo`, N+1 doble: una por owner y otra por provider, y cada
 *    una a su vez consulta la persona). Acá el pipeline los trae con `$lookup`.
 *
 * CÓMO SE REPARTE. El detalle publica la IDENTIDAD de la negociación —su
 * estado, sus fechas, sus partes—, y las otras tres lecturas cuelgan de sus
 * propios endpoints: el historial, los requisitos de adjuntos y los
 * FORMULARIOS. El legacy los mandaba todos juntos porque devolvía el documento
 * entero, no porque alguien decidiera que había que mandarlos: la pantalla pide
 * el detalle en cada pintada y las pestañas sólo cuando se abren.
 */

/** Referencia wire-safe a un catálogo: su id público y cómo se muestra. */
/**
 * Referencia wire-safe a un catálogo: su id público y cómo se muestra.
 *
 * **`code` es un STRING.** Se declaró `number` y eso hizo que TODOS los códigos viajaran
 * `null`: el presenter hacía `typeof row.code === 'number' ? row.code : null`, así que cada
 * código real —que es texto— se descartaba en silencio. Medido en `idc-real-estate-dev`:
 * inmuebles 383/383 string (`CNAST2502`), proyectos 8/8 string (`AS`), agencias 3/3 string
 * (`01`); empresas y sociedades no tienen el campo, y ahí `null` sí es correcto.
 *
 * No es un código numérico con formato: `CNAST2502` no cabe en un número. El contrato de la
 * app del cliente ya lo publicaba bien (`V2ClientPropertiesDto.code: string`) — el que
 * estaba mal era éste.
 */
const namedRef = <T extends z.ZodTypeAny>(id: T) =>
  z.object({ id, name: z.string(), code: z.string().nullable() })

/** Autoría y asesor: `usr_`, nunca el `_id` ni el documento del usuario. */
export const V2AdminNegotiationUserRefSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string(),
    avatar: z.string().nullable()
  })
  .nullable()

/**
 * El estado ACTUAL, con lo que la pantalla necesita para decidir qué ofrecer.
 *
 * `index` es la posición dentro del workflow y puede ser `null`: los estados globales
 * —`rescind`, `inactive`, el `end` sin workflow— viven fuera de la lista ordenada, y también
 * queda `null` el expediente parado en un estado que ya no figura en su flujo.
 *
 * ⚠️ **Se DERIVA del flujo; no es el `currentState.index` guardado.** Ese campo es una
 * desnormalización que puede quedar vacía con el expediente en un paso de proceso —pasó, por una
 * entrada de historial sin índice— y publicarlo tal cual hacía que el contrato afirmara una
 * posición falsa. Derivarla también hace que la FILA y el DETALLE del mismo expediente no puedan
 * contradecirse: los dos preguntan lo mismo a la misma función.
 */
export const V2AdminNegotiationCurrentStateSchema = z.object({
  state: z.object({
    id: PublicIdSchemas.state,
    name: z.string(),
    color: z.string().nullable(),
    type: z.string(),
    /** Si al entrar a este estado la negociación se empuja a SAP. */
    sendSAP: z.boolean()
  }),
  index: z.number().nullable(),
  stateAt: InstantSchema.nullable(),
  /** El estado se alcanzó retrocediendo desde el siguiente. */
  isRejected: z.boolean(),
  /** El cron diario la marcó vencida en este estado. */
  overdue: z.boolean()
})

/**
 * Un propietario de la negociación.
 *
 * `client` es el `cli_` y no el `_id` justamente para que la pantalla pueda
 * abrir su expediente. Los tres flags son de la RELACIÓN entre el cliente y esta
 * negociación —quién es el titular, a quién se le factura, con qué figura
 * participa—, no atributos del cliente: por eso viven acá y no en el expediente.
 */
export const V2AdminNegotiationOwnerSchema = z.object({
  client: z.object({
    id: PublicIdSchemas.client,
    /**
     * El código del cliente. Es un NÚMERO —el de los catálogos es texto— y la tabla de
     * personas relacionadas lo muestra en su primera columna, así que sin él la pantalla
     * no se puede migrar sin perder una columna.
     *
     * El servicio ya lo resolvía (`OwnerClient.code`, el mismo `select` que usan los
     * proveedores): lo que faltaba era publicarlo. Declararlo acá es lo que hace que
     * exista para el consumidor — un campo que el presenter arma pero el contrato no
     * declara, la serialización lo descarta.
     */
    code: z.number().nullable(),
    name: z.string(),
    typePerson: z.enum(['individual', 'juridical']),
    identification: z.string().nullable(),
    /**
     * El TIPO de documento —`D` DPI, `P` pasaporte, `CN` certificado de nacimiento…—, que no
     * es decoración: es lo que decide cómo se FORMATEA el número. La pantalla imprime un DPI
     * agrupado (`1234 56789 0101`) y un pasaporte tal cual vino, y sin el tipo no puede
     * distinguirlos, así que todos saldrían crudos.
     *
     * Es `null` para la persona jurídica —que se identifica por NIT, no por documento— y
     * también para el individual histórico que se cargó sin él: el índice único de
     * `person-individual` tiene `partialFilterExpression` justamente porque esos existen.
     */
    identificationType: z.enum(IDENTIFICATION_CARDS).nullable(),
    nit: z.string().nullable(),
    email: z.string().nullable(),
    /**
     * La foto del cliente, para el avatar de la lista de personas relacionadas.
     *
     * ⚠️ Vive en DOS lugares distintos según el tipo de persona: en el individual está en la
     * RAÍZ del documento (`PersonIndividual.photo`) y en la empresa dentro de `applicant`,
     * que es `Mixed`. Quien la lea tiene que mirar los dos — un `select` que traiga sólo
     * `applicant` deja sin foto a TODAS las personas individuales, que son la mayoría, y el
     * fallback de iniciales lo disimula lo suficiente como para que nadie lo reporte.
     */
    photo: z.string().nullable()
  }),
  /** El titular. Es el que encabeza los documentos de la negociación. */
  default: z.boolean(),
  invoice: z.boolean(),
  typeFigureProvider: z.string()
})

/** Las tres fechas del ciclo. `end` sólo existe cuando la negociación cerró. */
export const V2AdminNegotiationDatesSchema = z.object({
  start: InstantSchema.nullable(),
  update: InstantSchema.nullable(),
  end: InstantSchema.nullable()
})

/**
 * Dónde se firmó la negociación.
 *
 * **`department` y `municipality` son NÚMEROS, no texto.** Son los códigos del catálogo
 * geográfico de Guatemala, y así están guardados: medido en `idc-real-estate-dev`, los 172
 * documentos tienen `int` en los dos campos. Publicarlos como `string` obligaría a la
 * pantalla a convertirlos de vuelta para poder buscar el nombre.
 *
 * `country` sí es texto: es el ISO-3166 alfa-2 (`GT` en los 172). Se deja `string` abierto y
 * no un enum porque el catálogo de países es externo y completo — acotarlo a lo que hoy hay
 * en la base convertiría una venta en el extranjero en un dato inválido.
 *
 * El bloque viaja siempre y son sus campos los que pueden faltar, igual que `dates`: la
 * negociación siempre ocurrió en algún lado, aunque no se haya cargado dónde.
 */
export const V2AdminNegotiationPlaceSchema = z.object({
  country: z.string().nullable(),
  department: z.number().nullable(),
  municipality: z.number().nullable()
})

/**
 * Cómo se clasifica la procedencia de la negociación.
 *
 * Los cinco valores salen del catálogo del dominio, no de lo que hoy tenga la base: en
 * `idc-real-estate-dev` sólo aparecen `REPURCHASE` (52), `OTHER` (19) y `NEIGHBOURS` (15),
 * pero `DEPARTMENTAL` e `INTERNATIONAL` son legítimos y todavía nadie los usó.
 *
 * Se declara con sus valores y no como `string` suelto por la misma razón que
 * `referral.concept`: la pantalla los usa de CLAVE para traducirlos.
 */
export const UBICATION_NEGOTIATIONS = [
  'REPURCHASE',
  'NEIGHBOURS',
  'DEPARTMENTAL',
  'INTERNATIONAL',
  'OTHER'
] as const

/**
 * Cómo se enteró el cliente del proyecto. Las dieciséis del catálogo del dominio.
 *
 * En la base hay además **cadena vacía** (4 documentos) y `null` (3). El vacío NO es un
 * valor: viaja `null`, igual que `funding.ownFunds`. Un `''` no es ninguna de las
 * dieciséis opciones y la pantalla lo traduciría a `undefined`.
 */
export const CONTACT_METHODS = [
  'NEIGHBOURS',
  'REFERRED',
  'REPURCHASE',
  'RENTAL',
  'FACEBOOK',
  'WEBSITE',
  'BILLBOARD_PERIFERICO',
  'BILLBOARD_CN',
  'OTHER_BILLBOARD',
  'OTHER_MAGAZINE',
  'TV',
  'EMAIL',
  'NARANJO_MALL',
  'EVENT',
  'INSTAGRAM',
  'OTHER'
] as const

/**
 * **Hacia dónde se mueve este expediente, y qué puede hacer QUIEN PREGUNTA.**
 *
 * Existe porque la misma cuenta se hacía en dos lugares con reglas distintas. El panel del
 * tablero recorría `workflow.states` por su cuenta buscando `order + 1` — y los `order` de un
 * flujo **tienen huecos por diseño**: borrar un estado lo saca de los flujos con `$pull`, que
 * conserva los `order` de los que quedan. El servidor ya lo resolvía bien; la pantalla no.
 *
 * Publicarlo acá tiene un segundo efecto: para hacer esa cuenta el navegador necesitaba la lista
 * interna de estados del flujo, que **sólo publica el detalle v1**. Sin la cuenta, tampoco
 * necesita el dato.
 */
export const V2AdminNegotiationTransitionsSchema = z.object({
  /**
   * La posición dentro del flujo. `null` cuando el expediente no lo está recorriendo —una venta
   * cerrada, una rescindida y una inactiva no están en ningún paso, y de hecho su estado ni
   * figura en el flujo—.
   */
  order: z.number().nullable(),
  /**
   * El estado al que AVANZARÍA, ya resuelto: el paso siguiente del flujo, o el estado terminal
   * cuando no queda ninguno. Es el destino real, así que la pantalla puede nombrarlo («Enviar a
   * PREPARACIÓN LEGAL») y distinguir el avance del cierre mirando su `type`.
   *
   * `null` cuando no hay a dónde avanzar.
   */
  next: z.object({ id: PublicIdSchemas.state, name: z.string(), type: z.string() }).nullable(),
  /**
   * Si quien pregunta puede AVANZAR este expediente.
   *
   * ⚠️ No es «tiene `ExecuteStateTransition`». Es **el mismo predicado de tres partes** que la
   * API aplica al ejecutar (`assertCanTransition`): ver el estado actual, ejecutar hacia el
   * destino **y poder leer el destino** — mover algo a un estado que después no se ve lo saca
   * del tablero de quien lo movió.
   *
   * La pantalla comprobaba sólo la del medio, así que le ofrecía el botón a quien la API iba a
   * rechazar con 403. Medido contra `dev` el 2026-08-06: hay **1 par rol/estado** con
   * `Execute` sin `Read` — poco, pero es una promesa rota y ahora no puede volver a abrirse,
   * porque la respuesta la da el mismo predicado que decide.
   */
  canAdvance: z.boolean(),
  /**
   * Si el expediente puede DEVOLVERSE al paso anterior.
   *
   * ⚠️ A diferencia de `canAdvance`, esto **no comprueba permiso de transición** — y es fiel al
   * servidor, no un descuido: `RejectNegotiationUseCase` es el único de los cinco que no llama a
   * `assertCanTransition`, así que alcanza con poder ver el expediente. Es el hueco de la
   * decisión Nº 10, fijado con tests a los dos lados para que el día que se responda caigan
   * juntos.
   *
   * Publicarlo igual —en vez de dejar que la pantalla mire `order > 1`— tiene sentido porque
   * **cuál es el paso anterior** sí es cuenta del flujo, con los mismos huecos que `next`.
   */
  canReject: z.boolean()
})

export type V2AdminNegotiationTransitions = z.infer<typeof V2AdminNegotiationTransitionsSchema>

export const V2AdminNegotiationDetailSchema = z.object({
  id: PublicIdSchemas.negotiation,
  code: z.number(),
  currentState: V2AdminNegotiationCurrentStateSchema,
  workflow: z.object({ id: PublicIdSchemas.workflow, name: z.string() }).nullable(),
  dates: V2AdminNegotiationDatesSchema,
  place: V2AdminNegotiationPlaceSchema,

  company: namedRef(PublicIdSchemas.company).nullable(),
  agency: namedRef(PublicIdSchemas.agency).nullable(),
  society: namedRef(PublicIdSchemas.society).nullable(),
  project: namedRef(PublicIdSchemas.project).extend({ currency: z.string().nullable() }).nullable(),
  /**
   * La propiedad de la negociación. Es NULLABLE y no por descuido: al rescindir,
   * el legacy pone `property: null` y mueve la anterior a `previousProperty`, que
   * es cómo se sabe qué inmueble se liberó.
   */
  property: namedRef(PublicIdSchemas.property)
    .extend({ typeProperty: PublicIdSchemas.typeProperty.nullable() })
    .nullable(),
  previousProperty: namedRef(PublicIdSchemas.property)
    .extend({ typeProperty: PublicIdSchemas.typeProperty.nullable() })
    .nullable(),

  advisor: V2AdminNegotiationUserRefSchema,
  owners: z.array(V2AdminNegotiationOwnerSchema),
  transitions: V2AdminNegotiationTransitionsSchema,

  currency: z.string().nullable(),
  riskLevel: z.string().nullable(),

  /**
   * De dónde viene la negociación y cómo llegó el cliente.
   *
   * Los dos «otros» son el texto libre que acompaña a `OTHER` y sólo tienen sentido cuando
   * su campo vale eso: la pantalla muestra el campo extra únicamente en ese caso. Viajan
   * igual —el contrato publica lo que hay, no lo que la pantalla decida mostrar—, pero por
   * eso son nullable y no cadena vacía.
   */
  ubicationNegotiation: z.enum(UBICATION_NEGOTIATIONS).nullable(),
  otherUbicationNegotiation: z.string().nullable(),
  contactMethod: z.enum(CONTACT_METHODS).nullable(),
  otherContactMethod: z.string().nullable(),

  /** Estado del empuje a SAP. `orderSAP` es el número de orden que SAP devolvió. */
  orderSAP: z.number().nullable(),
  isSendSAP: z.boolean(),

  isDelivered: z.boolean(),
  deliveredAt: InstantSchema.nullable(),

  createdBy: V2AdminNegotiationUserRefSchema,
  updatedBy: V2AdminNegotiationUserRefSchema,
  createdAt: InstantSchema.nullable(),
  updatedAt: InstantSchema.nullable()
})

/**
 * Un paso del historial: a qué estado se movió, cuándo y quién.
 *
 * Es su propio endpoint y no un campo del detalle porque el legacy ya lo excluía
 * de la respuesta (`history: 0`) — el detalle se pide en cada pintada de la
 * pantalla y el historial sólo cuando se abre su pestaña. Publicarlo adentro
 * haría pagar el historial completo a todos los que no lo miran.
 */
export const V2AdminNegotiationHistoryEntrySchema = z.object({
  state: z.object({
    id: PublicIdSchemas.state,
    name: z.string(),
    color: z.string().nullable(),
    type: z.string()
  }),
  index: z.number().nullable(),
  /**
   * El flujo al que pertenece ese paso, cuando la transición CAMBIÓ de flujo.
   *
   * Viaja porque la pantalla lo muestra: una negociación puede saltar de un workflow a
   * otro —creación → renovación, por ejemplo— y sin esto la tabla pierde el corte que
   * explica por qué el recorrido de estados cambia de forma a mitad de camino.
   */
  workflow: z.object({ id: PublicIdSchemas.workflow, name: z.string() }).nullable(),
  /** Cuándo ENTRÓ a ese estado. */
  at: InstantSchema.nullable(),
  /**
   * Cuándo SALIÓ. `null` significa que sigue ahí — la pantalla lo pinta «Vigente».
   *
   * No se deriva de la entrada siguiente: v1 lo **guarda** en la fila al transicionar
   * (`item.endDate = new Date()`), así que derivarlo sería inventar un dato que ya existe y
   * discrepar de él en cuanto una entrada se borre o llegue desordenada.
   *
   * Sin este campo la pantalla que este endpoint tiene que reemplazar no se puede pintar:
   * muestra una columna «Hasta» y la DURACIÓN de cada estado, y las dos salen de acá.
   */
  until: InstantSchema.nullable(),
  by: V2AdminNegotiationUserRefSchema,
  /**
   * El comentario con el que se movió a ESTE estado, tal como está guardado.
   *
   * Ojo al migrar la pantalla v1: ahí se lee el comentario de la fila SIGUIENTE
   * (`nextEntry?.comment`), porque quien mira una fila quiere saber por qué SALIÓ de ese
   * estado. Es una decisión de presentación, no del contrato: acá el comentario viaja
   * pegado a la transición que lo produjo.
   */
  comment: z.string().nullable(),
  isRejected: z.boolean()
})

export const V2AdminNegotiationHistorySchema = z.object({
  data: z.array(V2AdminNegotiationHistoryEntrySchema)
})

/**
 * Los adjuntos que el estado ACTUAL exige para poder avanzar.
 *
 * Depende del estado, no de la negociación: es la configuración del estado en el
 * que está parada. Por eso viaja con `state`, para que la pantalla pueda decir
 * de dónde sale la exigencia en vez de mostrar una lista sin contexto.
 */
export const V2AdminNegotiationAttachmentRequirementsSchema = z.object({
  state: z.object({ id: PublicIdSchemas.state, name: z.string() }).nullable(),
  data: z.array(
    z.object({
      id: PublicIdSchemas.attachmentRequirement,
      name: z.string(),
      required: z.boolean()
    })
  )
})

/**
 * Los FORMULARIOS del detalle: las pestañas que el legacy servía dentro del
 * mismo `GET /:id` porque devolvía el documento entero.
 *
 * Se modelan honestamente desde los schemas de Mongo —a diferencia de clientes,
 * que guardaba estas cosas como `Mixed` y obligó a inventar `openShape`—, así
 * que acá cada campo existe porque existe en la base, no porque alguien lo
 * adivinó.
 */

/** Un proveedor del negocio. Como los propietarios, viaja con su `cli_`. */
export const V2AdminNegotiationProviderSchema = z.object({
  client: z.object({
    id: PublicIdSchemas.client,
    /**
     * El código del cliente. Es un NÚMERO —a diferencia del `code` de los catálogos, que
     * es texto— y la tabla de proveedores lo muestra en su primera columna, así que sin
     * él la pantalla no se puede migrar sin perder una columna.
     */
    code: z.number().nullable(),
    name: z.string(),
    typePerson: z.enum(['individual', 'juridical'])
  }),
  relation: z.string(),
  reason: z.string(),
  amount: z.number()
})

/**
 * Origen de los fondos declarado para el negocio.
 *
 * Los dos campos son CÓDIGOS de catálogo, no texto libre, y se declaran con sus valores
 * por la misma razón que `referral.concept`: la pantalla los usa de clave para traducirlos,
 * y un `string` abierto la obliga a indexar a ciegas.
 *
 * Los conjuntos salen del catálogo del dominio, no de lo que hoy tenga la base — en
 * `idc-real-estate-dev` sólo aparecen `1` (149 veces), `2`, `3` y `PI`, pero `PJ` y `PU`
 * son valores legítimos que todavía nadie usó.
 */
export const V2AdminNegotiationFundingSchema = z.object({
  /**
   * `1` propios · `2` de un tercero · `3` propios y de un tercero.
   *
   * Nullable porque un valor desconocido tiene que viajar como desconocido: el presenter
   * caía a `''`, que no es ninguna de las tres opciones y la pantalla habría traducido a
   * `undefined`.
   */
  ownFunds: z.enum(['1', '2', '3']).nullable(),
  /** `PI` persona individual · `PJ` jurídica · `PU` ambas. */
  thirdPartyFunds: z.enum(['PI', 'PJ', 'PU']).nullable(),
  providers: z.array(V2AdminNegotiationProviderSchema)
})

/**
 * Lo común entre el parqueo y la bodega: qué número tienen, cuánto miden y en qué nivel están.
 *
 * **`areaM2` faltaba y la pantalla lo muestra.** Existe en Mongo desde siempre
 * (`parking.schema.ts`, `basement.schema.ts`) y la vista del acuerdo de pago lo imprime en la
 * misma frase que el número y el nivel —«No. 146, 25 m², para 2 vehículo(s)…»—. No estaba
 * declarado acá ni en el tipo del documento que usa el servicio, así que el presenter no
 * podía verlo y nadie se quejó: es el mismo mecanismo que dejó muerto el historial, un tipo
 * escrito a mano que describe una suposición y un compilador que valida contra ella.
 *
 * Es `number` y no entero: medido en `idc-real-estate-dev`, hay bodegas de `2.56` m².
 */
const parkingOrBasement = z.object({
  number: z.number().nullable(),
  areaM2: z.number().nullable(),
  levelType: z.string().nullable(),
  level: z.number().nullable()
})

/** Una cuota del plan de pago. */
export const V2AdminNegotiationPaymentSchema = z.object({
  number: z.number(),
  date: LocalDateSchema.nullable(),
  amount: z.number(),
  upgrade: z.number().nullable(),
  comment: z.string().nullable()
})

/**
 * El acuerdo de pago.
 *
 * **`amountsWithoutTax` va aparte y es NULLABLE a propósito.** Los cuatro
 * montos sin impuestos están declarados como restringidos por
 * `FieldAmountsWithoutTaxNegotiation`, pero v1 NUNCA los filtró: los devuelve a
 * cualquiera que pueda leer la negociación, y el único control es que la WEB no
 * los pinta. Un control que vive sólo en el navegador no es un control — se ve
 * en la pestaña de red o llamando a la API directo.
 *
 * En v2 el permiso se aplica en el servidor: sin él, el bloque entero viaja
 * `null`. Agruparlos en vez de anular cuatro campos sueltos hace que la
 * ausencia sea LEGIBLE —«no tenés permiso para este bloque»— en vez de
 * parecerse a un acuerdo sin cargar.
 */
export const V2AdminNegotiationPaymentAgreementSchema = z.object({
  extraLand: z.number(),
  hasParkingAndBasement: z.boolean(),
  detailsParkingAndBasement: z.string().nullable(),
  hasParking: z.boolean(),
  parking: parkingOrBasement.extend({ vehicles: z.number().nullable() }).nullable(),
  hasBasement: z.boolean(),
  basement: parkingOrBasement.extend({ storageNumber: z.number().nullable() }).nullable(),
  sourceFundsForPayment: z.array(z.string()),
  otherSourceFundsForPayment: z.string().nullable(),
  methodPayment: z.array(z.string()),
  propertyPaymentMethod: z.string().nullable(),
  totalAmount: z.number(),
  reserve: z.number().nullable(),
  deliveryDate: LocalDateSchema.nullable(),
  payments: z.array(V2AdminNegotiationPaymentSchema),
  /** Restringido por permiso. `null` = quien pregunta no puede verlos. */
  amountsWithoutTax: z
    .object({
      title: z.number().nullable(),
      total: z.number().nullable(),
      upgrades: z.number().nullable(),
      property: z.number().nullable()
    })
    .nullable()
})

/** Quién refirió el negocio, si vino referido. */
/**
 * Qué clase de promoción es. Se declara con sus cuatro valores y no como `string` suelto
 * porque la pantalla los usa de CLAVE para traducirlos, y un `string` abierto la obliga a
 * indexar a ciegas.
 *
 * Medido en `idc-real-estate-dev` antes de cerrarlo: los únicos valores presentes son
 * `REPURCHASE` (37), `REFERRAL` (29), `RENT` (12) y `NOT_APPLICABLE` (7), más 87 en `null`.
 * No hay cola de valores viejos que este enum deje afuera.
 *
 * Vive acá y no inline porque lo usan la LECTURA y la ESCRITURA. El servicio tenía su propia
 * copia para validar contra ella al presentar; ahora las tres miran la misma lista.
 */
export const REFERRAL_CONCEPTS = ['NOT_APPLICABLE', 'REFERRAL', 'REPURCHASE', 'RENT'] as const

export const V2AdminNegotiationReferralSchema = z.object({
  concept: z.enum(REFERRAL_CONCEPTS).nullable(),
  personWhoRefers: z
    .object({ name: z.string(), phone: z.string(), codeProperty: z.string() })
    .nullable(),
  promotion: z.string().nullable()
})

/** Debida diligencia ampliada: tres campos de texto libre. */
export const V2AdminNegotiationDueDiligenceSchema = z.object({
  expansionSourceFunds: z.string().nullable(),
  expansionEconomicActivity: z.string().nullable(),
  other: z.string().nullable()
})

/** Una línea de la orden de producción, con sus tres niveles y su costo. */
export const V2AdminNegotiationProductionOrderSchema = z.object({
  level1: z.string(),
  level1D: z.string(),
  level2: z.string(),
  level2D: z.string(),
  level3: z.string(),
  level3D: z.string(),
  costUSD: z.number(),
  costQTZ: z.number()
})

/**
 * Los formularios del detalle, juntos y como SU PROPIO endpoint.
 *
 * No van dentro del detalle por la misma razón que el historial: la pantalla
 * pide el detalle en cada pintada y estas pestañas sólo cuando se abren. El
 * legacy los mandaba siempre porque devolvía el documento entero, no porque
 * alguien decidiera que había que mandarlos.
 *
 * Cada bloque es NULLABLE: son opcionales en el schema y una negociación recién
 * creada no tiene ninguno.
 */
export const V2AdminNegotiationFormsSchema = z.object({
  funding: V2AdminNegotiationFundingSchema.nullable(),
  paymentAgreement: V2AdminNegotiationPaymentAgreementSchema.nullable(),
  referral: V2AdminNegotiationReferralSchema.nullable(),
  dueDiligence: V2AdminNegotiationDueDiligenceSchema.nullable(),
  productionOrder: z.array(V2AdminNegotiationProductionOrderSchema)
})

export type V2AdminNegotiationForms = z.infer<typeof V2AdminNegotiationFormsSchema>
export type V2AdminNegotiationDetail = z.infer<typeof V2AdminNegotiationDetailSchema>
export type V2AdminNegotiationHistory = z.infer<typeof V2AdminNegotiationHistorySchema>
export type V2AdminNegotiationAttachmentRequirements = z.infer<
  typeof V2AdminNegotiationAttachmentRequirementsSchema
>

/**
 * La LIBERACIÓN del acceso móvil (ADR 0024).
 *
 * Devuelve un resultado POR DUEÑO y no un booleano, porque el caso interesante es el
 * parcial: una negociación con dos propietarios donde uno tiene correo y el otro no
 * quedó a medias, y quien apretó el botón tiene que ver cuál falta. Un `204` obligaría a
 * ir a buscar a mano qué pasó.
 *
 * Y por eso mismo NO tira ante un dueño sin correo: la venta ya está cerrada, el acceso
 * es lo que quedó pendiente. Un error acá haría que un correo mal cargado se lea como un
 * fallo del cierre.
 */
export const V2MobileAccessOwnerResultSchema = z.object({
  /**
   * El `cli_` del dueño. NO el ObjectId: el interceptor de contrato v2 rechaza un id
   * legacy en cualquier campo `*Id`, así que devolverlo hacía fallar el endpoint entero
   * con 500 ante cualquier dueño real.
   */
  clientId: PublicIdSchemas.client,
  /**
   * Si es el propietario PRINCIPAL: el `default: true` del expediente, el mismo que la
   * pantalla de dueños llama «predeterminado para la escritura y las facturas».
   *
   * Viaja porque sin él la lista de dueños no se puede leer: bajo el alcance vigente el
   * principal recibe acceso automático y el copropietario no, así que dos filas con el
   * mismo `status: 'pending'` significan cosas distintas —una es un pendiente, la otra es
   * la política funcionando— y la pantalla no tiene cómo distinguirlas sola.
   */
  principal: z.boolean().default(false),
  status: z.enum(['granted', 'pending']),
  /**
   * Por qué quedó pendiente. `no-email` es el caso que el negocio tiene que resolver;
   * `email-taken` es un cliente que comparte correo con otra cuenta —un matrimonio con
   * un solo mail— y `account-blocked` es una decisión de seguridad que un flujo de
   * negocio NO puede revertir sin que alguien lo mire.
   *
   * `not-in-scope` es el de la decisión Nº 28: el copropietario **no está pendiente de
   * nada**, la liberación automática no lo alcanza por política y hay que habilitarlo a
   * pedido. Se publica en vez de omitir la fila porque omitirla es el defecto que esa
   * decisión vino a cerrar — una lista corta es indistinguible de «no había a quién dar
   * acceso», y así nadie se entera de que falta alguien.
   */
  reason: z.enum(['no-email', 'email-taken', 'account-blocked', 'not-in-scope']).optional(),
  accountId: PublicIdSchemas.clientAccount.optional(),
  grantId: PublicIdSchemas.accessGrant.optional(),
  /**
   * Si a esta persona se le mandó la invitación EN ESTA corrida.
   *
   * Es un dato distinto de `status`: el grant se emite siempre, la invitación sólo si la
   * cuenta nunca se usó. Un cliente que compra su segundo inmueble sale `granted` con
   * `invited: false`, y está bien — ya tiene la app.
   */
  invited: z.boolean().default(false)
})

/**
 * El CUERPO de la liberación. Todo opcional: sin nada, es la corrida automática de siempre.
 *
 * ── `ownerId` ES UN ACTO DISTINTO, NO UN FILTRO ─────────────────────────────
 *
 * Sin `ownerId`, la liberación alcanza a quien diga la política —hoy, sólo al propietario
 * principal (decisión Nº 28)—. **Con `ownerId`, alcanza a esa persona y la política NO se
 * aplica**, que es exactamente para lo que existe: el «se le puede enviar también al
 * copropietario por si lo solicitan».
 *
 * Se escribe como un campo y no como un endpoint aparte porque el EFECTO es el mismo
 * —asegurar la cuenta y emitir el grant, idempotente— y duplicarlo dejaría dos caminos que
 * hay que mantener sincronizados. Lo que cambia es a quién, y eso es un parámetro.
 *
 * Nombrar a alguien que no es dueño de la negociación es un `404`: la lista de dueños es la
 * que manda, no quien llama.
 */
export const V2MobileAccessReleaseRequestSchema = z.object({
  /** El `cli_` del dueño a habilitar. Omitilo para la liberación automática. */
  ownerId: PublicIdSchemas.client.optional()
})

export type V2MobileAccessReleaseRequest = z.infer<typeof V2MobileAccessReleaseRequestSchema>

export const V2MobileAccessReleaseSchema = z.object({
  /** `null` cuando la negociación no tiene inmueble — rescindir lo pone en `null`. */
  propertyId: PublicIdSchemas.property.nullable(),
  owners: z.array(V2MobileAccessOwnerResultSchema)
})

export type V2MobileAccessRelease = z.infer<typeof V2MobileAccessReleaseSchema>

/**
 * El ESTADO del acceso móvil, que es la mitad de lectura de la misma acción.
 *
 * Existe porque el ADR 0024 pide que un acceso que no se pudo liberar quede **visible**,
 * y un toast no es un estado: se va con el siguiente render y con cualquier recarga. Si
 * la única señal de que un cliente quedó sin acceso dura cinco segundos en la pantalla de
 * quien aprobó, el modo de falla es exactamente el que el ADR quería evitar —nadie se
 * entera hasta que el cliente llama—.
 *
 * Se calcula, no se guarda. El acceso ES la cuenta más el grant; un campo `accessStatus`
 * al lado sería un segundo lugar donde vive la misma verdad, y el día que alguien revoque
 * un grant desde la pantalla de cuentas quedaría mintiendo.
 */
export const V2MobileAccessOwnerStatusSchema = V2MobileAccessOwnerResultSchema.extend({
  /**
   * Cómo se llama el dueño. Viaja acá porque el aviso útil nombra a la persona —«Ana
   * Pérez no tiene correo»— y la pantalla que lo muestra no siempre tiene los dueños
   * cargados: el botón vive en la columna de acciones, no en la tabla de propietarios.
   */
  name: z.string(),
  /**
   * La FOTO del expediente de esa persona, o `null`.
   *
   * Viaja acá por la misma razón que `name`: la pantalla que muestra estas filas es la única que
   * pone cara a quién puede abrir la app, y no tiene los expedientes de los dueños cargados. Es
   * además la fuente de la cara que dibuja el preview cuando la consola previsualiza a un
   * copropietario — el contexto del preview publica la del PRINCIPAL, así que sin esto elegir a
   * otra persona cambiaba el nombre y dejaba la cara del primero.
   *
   * `null` es lo NORMAL —dos de cada diez clientes no tienen foto cargada— y quien la dibuja cae
   * a las iniciales. ☠️ La foto vive en dos lugares según el tipo de persona
   * (`v2/clients/client-avatar.ts`).
   */
  avatar: z.string().nullable().default(null),
  /**
   * `not-released` es el reason que sólo puede aparecer en la LECTURA: el cliente está en
   * condiciones de recibir acceso y nadie corrió la acción todavía. En la escritura no
   * existe, porque después de correrla ese caso ya no puede quedar.
   *
   * `expired` también es sólo de lectura, y es el que NO se puede confundir con
   * `not-released`: hubo acceso y se le cumplió el plazo. El vencimiento no escribe nada
   * —el grant conserva su `active: true`—, así que sin este valor la tarjeta decía «acceso
   * concedido» mientras el teléfono ya responde `CLIENT_APP_ACCESS_EXPIRED`. Y decir
   * «falta liberar el acceso» tampoco servía: manda a buscar un paso que ya se hizo.
   */
  reason: z
    .enum(['no-email', 'email-taken', 'account-blocked', 'not-in-scope', 'not-released', 'expired'])
    .optional(),
  /**
   * Si la persona YA ENTRÓ alguna vez a la app.
   *
   * Sale de `ClientAccount.firstAccessAt` y NO de si tiene sesiones abiertas: esa colección
   * tiene TTL, así que «sin sesiones» significa lo mismo para quien nunca entró que para
   * quien entró hace tres meses. La pantalla necesita distinguirlos — «invitado, todavía
   * no ingresó» es un pendiente que alguien puede empujar; «ya ingresó» no lo es.
   */
  signedIn: z.boolean().default(false),
  /** Cuándo se le mandó la última invitación, si se le mandó alguna. */
  invitedAt: InstantSchema.nullable().default(null)
})

export const V2MobileAccessStatusSchema = z.object({
  /** `null` cuando la negociación no tiene inmueble — rescindir lo pone en `null`. */
  propertyId: PublicIdSchemas.property.nullable(),
  owners: z.array(V2MobileAccessOwnerStatusSchema)
})

export type V2MobileAccessStatus = z.infer<typeof V2MobileAccessStatusSchema>

/* ────────────────────────────────────────────────────────────────────────────
 * ESCRITURA — la edición del expediente
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Contrato de ESCRITURA del expediente. `PATCH /admin/negotiations/:id`.
 *
 * **MERGE POR SECCIÓN, y eso reemplaza a los `field guards` de v1.** Un bloque que no viene
 * NO SE TOCA; uno que viene se reemplaza entero. Es la misma regla que la edición de clientes
 * (`V2UpdateClientInput`) y no es sólo simetría: v1 mandaba TODOS los bloques siempre al
 * `$set`, así que una pantalla que editaba un dato borraba otro que ni mostraba. Se defendió
 * con `applyFieldGuards`, que compara contra el documento vivo y protege lo que llegaría
 * vacío, más un `Sentry.captureMessage` por cada rescate.
 *
 * Un guard es la respuesta correcta cuando no podés cambiar el contrato. Acá se puede: si el
 * bloque ausente no se toca, **el error deja de ser posible** y no hace falta vigilarlo. El
 * guard de v1 se queda donde está mientras v1 exista; no se copia.
 *
 * **TODAS LAS REFERENCIAS VAN POR ID PÚBLICO.** v1 las pedía con `@IsMongoId()`, y eso es lo
 * que hace que el selector de clientes de negociaciones no pueda migrar: lo que elige termina
 * siendo `owners[].client` de un POST. Acá viajan `cmp_`, `agc_`, `soc_`, `prj_`, `prp_`,
 * `usr_` y `cli_`.
 *
 * ── LO QUE ESTE CONTRATO NO ACEPTA, Y POR QUÉ ────────────────────────────────
 *
 * · **`code`, `currentState`, `history`** — los gobierna el ciclo de vida, no una edición.
 * · **`orderSAP` / `isSendSAP`** — los gobierna SAP. Además el propio update los RESETEA al
 *   cambiar de inmueble, así que aceptarlos del cliente sería pelearle a esa regla.
 * · **`dates.end`** — v1 lo acepta en el body y **ningún camino de edición lo escribe**: las
 *   únicas escrituras de `dates.end` están en las TRANSICIONES (cierre y rescisión), que lo
 *   sellan con la fecha del momento. Un contrato que lo acepta deja estampar una fecha de
 *   cierre en una negociación abierta.
 * · **`dates.update`** — es `updatedAt` con otro nombre. Lo pone el servidor.
 * · **`payments[].id`** — el `id` de una cuota es una key de React, no la identidad de una
 *   entidad. Misma decisión que las filas repetibles del expediente del cliente.
 */

/** Una cuota del plan, tal como la manda el formulario. Sin `id`: ver el docblock. */
export const V2NegotiationPaymentInputSchema = z.object({
  number: z.number().int(),
  date: LocalDateSchema,
  amount: z.number(),
  upgrade: z.number().optional(),
  comment: z.string().optional()
})

const parkingOrBasementInput = z.object({
  number: z.number().nullish(),
  areaM2: z.number().nullish(),
  levelType: z.string().nullish(),
  level: z.number().nullish()
})

export const V2NegotiationPaymentAgreementInputSchema = z.object({
  extraLand: z.number().optional(),
  hasParkingAndBasement: z.boolean().optional(),
  detailsParkingAndBasement: z.string().nullish(),
  hasParking: z.boolean().optional(),
  parking: parkingOrBasementInput.extend({ vehicles: z.number().nullish() }).nullish(),
  hasBasement: z.boolean().optional(),
  basement: parkingOrBasementInput.extend({ storageNumber: z.number().nullish() }).nullish(),
  sourceFundsForPayment: z.array(z.string()),
  otherSourceFundsForPayment: z.string().nullish(),
  methodPayment: z.array(z.string()),
  propertyPaymentMethod: z.string().nullish(),
  totalAmount: z.number(),
  reserve: z.number().nullish(),
  deliveryDate: LocalDateSchema,
  payments: z.array(V2NegotiationPaymentInputSchema),
  /**
   * Los cuatro montos sin impuestos, agrupados igual que en la lectura.
   *
   * En la LECTURA el bloque viaja `null` sin el permiso; en la ESCRITURA, mandarlo sin el
   * permiso tiene que ser un rechazo y no un descarte silencioso — quien edita sin poder ver
   * estos montos los estaría mandando a ciegas.
   */
  amountsWithoutTax: z
    .object({
      title: z.number().nullish(),
      total: z.number().nullish(),
      upgrades: z.number().nullish(),
      property: z.number().nullish()
    })
    .nullish()
})

/** Origen de fondos y proveedores. El proveedor va por `cli_`, como el propietario. */
export const V2NegotiationFundingInputSchema = z.object({
  ownFunds: z.enum(['1', '2', '3']),
  thirdPartyFunds: z.enum(['PI', 'PJ', 'PU']).nullish(),
  providers: z
    .array(
      z.object({
        client: PublicIdSchemas.client,
        relation: z.string(),
        reason: z.string(),
        amount: z.number()
      })
    )
    .optional()
})

export const V2NegotiationReferralInputSchema = z.object({
  concept: z.enum(REFERRAL_CONCEPTS).nullish(),
  personWhoRefers: z
    .object({
      name: z.string().optional(),
      phone: z.string().optional(),
      codeProperty: z.string().optional()
    })
    .nullish(),
  promotion: z.string().nullish()
})

export const V2NegotiationDueDiligenceInputSchema = z.object({
  expansionSourceFunds: z.string().nullish(),
  expansionEconomicActivity: z.string().nullish(),
  other: z.string().nullish()
})

/**
 * Una línea de la orden de producción.
 *
 * Los `*D` son las DESCRIPCIONES y viajan con la línea a propósito: la negociación las
 * guarda desnormalizadas porque es la ficha de una venta firmada, y si mañana renombran una
 * opción en el catálogo de SAP el expediente tiene que seguir diciendo lo que se acordó.
 * Pedirlas al escribir es lo que hace que la lectura no dependa de SAP para pintar nombres.
 */
export const V2NegotiationProductionOrderInputSchema = z.object({
  level1: z.string(),
  level1D: z.string(),
  level2: z.string(),
  level2D: z.string(),
  level3: z.string(),
  level3D: z.string(),
  costUSD: z.number().optional(),
  costQTZ: z.number().optional()
})

/** Un propietario. Los tres flags son de la RELACIÓN, no del cliente. */
export const V2NegotiationOwnerInputSchema = z.object({
  client: PublicIdSchemas.client,
  default: z.boolean(),
  invoice: z.boolean(),
  typeFigureProvider: z.string()
})

export const V2UpdateNegotiationInputSchema = z.object({
  /** Sólo `start`: `update` y `end` los gobierna el ciclo de vida. Ver el docblock. */
  dates: z.object({ start: LocalDateSchema }).optional(),
  place: z
    .object({
      country: z.string(),
      department: z.number().int(),
      municipality: z.number().int()
    })
    .optional(),

  advisor: PublicIdSchemas.user.nullish(),
  company: PublicIdSchemas.company.optional(),
  agency: PublicIdSchemas.agency.optional(),
  society: PublicIdSchemas.society.optional(),
  project: PublicIdSchemas.project.optional(),
  property: PublicIdSchemas.property.optional(),

  currency: z.string().optional(),
  riskLevel: z.string().nullish(),
  ubicationNegotiation: z.enum(UBICATION_NEGOTIATIONS).nullish(),
  otherUbicationNegotiation: z.string().nullish(),
  contactMethod: z.enum(CONTACT_METHODS).nullish(),
  otherContactMethod: z.string().nullish(),

  owners: z.array(V2NegotiationOwnerInputSchema).optional(),
  funding: V2NegotiationFundingInputSchema.optional(),
  paymentAgreement: V2NegotiationPaymentAgreementInputSchema.optional(),
  referral: V2NegotiationReferralInputSchema.optional(),
  dueDiligence: V2NegotiationDueDiligenceInputSchema.optional(),
  productionOrder: z.array(V2NegotiationProductionOrderInputSchema).optional()
})

export type V2UpdateNegotiationInput = z.infer<typeof V2UpdateNegotiationInputSchema>

/**
 * El alta del expediente.
 *
 * ## Por qué NO es `Update` con `.required()`
 *
 * Se parecen, pero dicen cosas distintas y mezclarlas produce el peor error de un `PATCH`: un
 * campo ausente en el alta es un campo que FALTA, y en la edición es «no lo toques». Declararlos
 * por separado es lo que deja que el alta exija las seis referencias sin que la edición tenga que
 * pedirlas de nuevo cada vez.
 *
 * ## Qué es obligatorio, y por qué justo eso
 *
 * SIETE y sólo siete: las cinco referencias —`company`, `agency`, `society`, `project`,
 * `property`— más `dates` y `place`. **`advisor` es opcional**, que es el que más se presta a
 * confusión.
 *
 * Esa lista salió de los `@IsNotEmpty()` del `CreateNegotiationDto` de v1, verificada contra el
 * DTO el 2026-07-28 —antes acá decía «seis»—. **El DTO se borró el 2026-08-05** con el resto del
 * contrato de escritura legacy, así que la lista se escribe entera acá en vez de apuntar a un
 * archivo que ya no existe: una justificación que remite a código muerto no se puede verificar.
 *
 * No se endurece ni se afloja nada: el alta v2 tiene que aceptar lo mismo que la v1 o el
 * formulario que hoy funciona dejaría de funcionar al recablearse.
 *
 * ## Lo que el servidor pone, y el cliente no manda
 *
 * `code` (contador), `workflow` y `currentState` (la definición por defecto de la acción `NEW`),
 * `history` (su primera entrada) y `createdBy`. Nada de eso es del cliente: son el estado inicial
 * de la máquina, y aceptarlos por el cuerpo sería dejar crear un expediente ya empezado.
 *
 * `currency` es OPCIONAL a propósito: si no viene, v1 la hereda del proyecto. Mandarla vacía y
 * heredarla son la misma cosa, así que el contrato no obliga a averiguarla antes de crear.
 *
 * `advisor` también, y con una regla que no es evidente: cuando quien crea es un ASESOR, v1 ignora
 * lo que venga y se asigna a sí mismo. El contrato lo acepta igual —un administrador sí elige— y
 * la regla vive donde ya vivía, no duplicada acá.
 *
 * ## Lo que quedó afuera
 *
 * `group`, que v1 acepta como ObjectId opcional. No tiene lectura publicada en v2 ni consumidor
 * en el formulario web, así que publicarlo sería inventar contrato. Si aparece un consumidor,
 * entra como referencia por id público, nunca como el ObjectId crudo.
 */
export const V2CreateNegotiationInputSchema = V2UpdateNegotiationInputSchema.extend({
  dates: z.object({ start: LocalDateSchema }),
  place: z.object({
    country: z.string(),
    department: z.number().int(),
    municipality: z.number().int()
  }),
  company: PublicIdSchemas.company,
  agency: PublicIdSchemas.agency,
  society: PublicIdSchemas.society,
  project: PublicIdSchemas.project,
  property: PublicIdSchemas.property
})

export type V2CreateNegotiationInput = z.infer<typeof V2CreateNegotiationInputSchema>

/**
 * Lo que devuelve el alta: el `neg_` y el código legible.
 *
 * El `code` viaja porque es lo que la persona ve y dice en voz alta —«la 168»—, y el contador que
 * lo asigna vive en el servidor: sin devolverlo, el formulario tendría que ir a buscarlo con una
 * segunda lectura para poder mostrar lo que acaba de crear.
 */
export const V2CreateNegotiationResultSchema = z.object({
  id: PublicIdSchemas.negotiation,
  code: z.number().int()
})

export type V2CreateNegotiationResult = z.infer<typeof V2CreateNegotiationResultSchema>

/**
 * Lo que devuelve la EDICIÓN. Sólo el id: el `code` no cambia al editar, y devolverlo invitaría a
 * creer que puede cambiar.
 *
 * Existe desde el 2026-08-05 y el motivo importa: hasta entonces el `PATCH` **no declaraba ninguna
 * respuesta**, y su `200` salía del default implícito que `@nestjs/swagger` agrega cuando una
 * operación no declara ninguna. Al declararle las respuestas de ERROR, ese default desapareció y el
 * SDK se quedó **sin tipo de retorno** para la edición. Un efecto que no se ve leyendo el
 * decorador que uno agrega, sólo comparando el contrato antes y después.
 */
export const V2UpdateNegotiationResultSchema = z.object({
  id: PublicIdSchemas.negotiation
})

export type V2UpdateNegotiationResult = z.infer<typeof V2UpdateNegotiationResultSchema>

/**
 * Total y facetas del tablero de negociaciones, con los mismos filtros allowlist
 * del listado y sin paginar. Es el recurso separado que el ADR 0019 exige: los
 * conteos NO viajan en el envelope del listado, así que móvil, PAT y las
 * integraciones que sólo recorren la colección no pagan el costo de calcularlos.
 *
 * Cada faceta ignora el filtro de SU PROPIA dimensión y respeta las demás. Si se
 * mirara a sí misma, al elegir un estado el resto saldría en cero y el tablero
 * afirmaría que no existen negociaciones en los otros — que es justamente el
 * dato que la persona necesita para seguir navegando. `total` sí respeta la
 * consulta completa.
 *
 * `byState` se indexa por `sta_`, nunca por el uuid legacy ni por `_id`: es la
 * misma llave con la que el chip filtra, así que el tablero cruza conteo y
 * filtro sin traducir nada. Ese cruce es exactamente el que hoy está roto entre
 * `my-transitions` v1 y el listado v2.
 *
 * Las dos dimensiones son de baja cardinalidad —trece estados, siete tipos— y
 * existen para navegar, no para reportar.
 *
 * LOS TRES NÚMEROS DE ACTIVIDAD —`createdThisMonth`, `closedThisMonth` y
 * `propertiesWithNegotiations`— viven acá y no en un `/metrics` aparte, aunque
 * el legacy publique dos rutas para ellos. Seis de los nueve números del panel
 * de KPI ya salían de este recurso, así que un segundo agregado sobre la misma
 * colección recalcularía `total`, `overdue` y `rejected` dos veces por carga y
 * reproduciría en v2 la duplicación que v1 arrastra.
 */
export const V2AdminNegotiationsSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  byState: z.record(PublicIdSchemas.state, z.number().int().nonnegative()),
  byStateType: z.record(z.string(), z.number().int().nonnegative()),
  overdue: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  /**
   * Altas del mes en curso, medidas sobre `createdAt`.
   *
   * El mes arranca en **UTC**, igual que en v1: el negocio opera en UTC−6, así
   * que las últimas seis horas del último día del mes local ya cuentan para el
   * siguiente. Se conserva la semántica heredada a propósito —cambiarla movería
   * números que alguien ya viene mirando— y se deja anotada para que sea una
   * decisión y no una sorpresa.
   */
  createdThisMonth: z.number().int().nonnegative(),
  /**
   * Ventas cerradas en el mes en curso: las que están en un estado de tipo `end`
   * y entraron a él este mes.
   *
   * ⚠️ **NO se mide sobre `currentState.stateAt`**, que es el campo que nombra
   * este dato. Ese campo guarda el texto largo de `String(new Date())` y
   * compararlo contra una fecha en Mongo es **siempre falso** —en el orden de
   * tipos de BSON un String va antes que un Date—, así que el equivalente de v1
   * devuelve cero con cualquier dato: julio de 2026 informó 0 sobre 13 ventas
   * reales.
   *
   * Se mide sobre `history[].startDate`, que sí es un `Date`: la última entrada
   * del historial es la que abre el estado actual. Verificado contra la base el
   * 2026-08-05 — coincide con el día que dice `stateAt` en **190 de 191**
   * documentos; el único que no es una importación legacy con otro huso.
   */
  closedThisMonth: z.number().int().nonnegative(),
  /**
   * Cuántos inmuebles DISTINTOS están involucrados. No es «negociaciones con
   * inmueble»: varias pueden apuntar al mismo, y la tarjeta lo contrasta contra
   * `total` («N activos vinculados a M negociaciones»), que es lo que hace
   * visible la concentración.
   */
  propertiesWithNegotiations: z.number().int().nonnegative()
})

export type V2AdminNegotiationsSummary = z.infer<typeof V2AdminNegotiationsSummarySchema>

/**
 * UNA FILA del tablero de negociaciones. No es el detalle recortado: es un
 * contrato propio, y esa diferencia es deliberada.
 *
 * El detalle sirve una pantalla que ya se abrió sobre UN expediente; la fila
 * sirve cien a la vez. Reusar el detalle acá arrastraría el acuerdo de pago, la
 * orden de producción y la diligencia ampliada —todo lo que la tabla no pinta—
 * multiplicado por el tamaño de página, y ataría las dos superficies: agregar un
 * campo al detalle engordaría el listado sin que nadie lo decidiera.
 *
 * LO QUE NO ESTÁ, Y ES EL PUNTO: los montos SIN IMPUESTO y `riskLevel`. Los dos
 * tienen permiso de campo —`FieldAmountsWithoutTaxNegotiation` y
 * `FieldRiskLevelNegotiation`— y una fila de listado no tiene dónde aplicar esa
 * decisión: se publicarían para todos. Quien los necesita abre el expediente,
 * que sí sabe filtrarlos.
 *
 * ⚠️ **CORRECCIÓN (2026-08-07): «que sí sabe filtrarlos» era falso para
 * `riskLevel`.** El detalle filtraba los montos sin impuesto y nada más; el
 * nivel de riesgo y la ubicación viajaban a cualquiera que pudiera leer el
 * expediente, con la web ocultándolos por su cuenta. La exclusión del listado
 * seguía siendo correcta —su motivo, no—. Cerrado el mismo día: el detalle
 * ahora filtra los tres.
 *
 * ⚠️ **CORRECCIÓN (2026-08-05): esto decía «ni montos» a secas, y era demasiado
 * amplio.** El permiso cubre `titleAmountWithoutTax`, `totalAmountWithoutTax`,
 * `upgradesAmountWithoutTax` y `propertyAmountWithoutTax` — los cuatro del
 * bloque sin impuesto, y sólo esos. `paymentAgreement.totalAmount` **no tiene
 * permiso de campo** y v1 lo publica en el tablero y en el dashboard desde
 * siempre. Excluirlo no protegía nada: dejaba a un consumidor legítimo sin un
 * dato que ya es público para quien puede leer la negociación.
 */
export const V2AdminNegotiationRowSchema = z.object({
  id: PublicIdSchemas.negotiation,
  code: z.number(),
  currentState: V2AdminNegotiationCurrentStateSchema,
  society: namedRef(PublicIdSchemas.society).nullable(),
  project: namedRef(PublicIdSchemas.project).nullable(),
  /** Nullable igual que en el detalle: al rescindir, el legacy la mueve a `previousProperty`. */
  property: namedRef(PublicIdSchemas.property).nullable(),
  /**
   * El inmueble que la negociación tenía antes de rescindirse. La columna de la
   * tabla hace `property ?? previousProperty`, así que sin este campo TODA
   * negociación rescindida mostraría la celda de inmueble vacía — que es
   * exactamente el tipo de regresión que no da error y nadie reporta hasta que
   * alguien busca una rescindida y no la reconoce.
   */
  previousProperty: namedRef(PublicIdSchemas.property).nullable(),
  advisor: V2AdminNegotiationUserRefSchema,
  /**
   * Sólo los propietarios, sin proveedores ni figuras: la columna de la tabla
   * muestra a quién pertenece la negociación, no su estructura societaria.
   */
  owners: z.array(
    z.object({
      id: PublicIdSchemas.client,
      name: z.string(),
      code: z.string().nullable(),
      /**
       * La foto del propietario, que es el AVATAR de la columna «Propietarios».
       *
       * El servicio ya la resolvía —`OwnerClient.photo`, la misma consulta que usa el
       * detalle, con el `select` que mira los dos lugares donde vive—: lo que faltaba
       * era publicarla. El presenter la descartaba y el contrato no la declaraba, así
       * que la columna caía SIEMPRE a las iniciales aunque el cliente tuviera foto
       * cargada — y falla callada, porque un avatar con iniciales es exactamente lo que
       * se ve cuando el cliente no tiene foto.
       *
       * Es la misma clase de hueco que dejó al detalle de cliente sin `avatar`: un campo
       * que el servicio tiene a mano pero el contrato no nombra, no existe para nadie.
       */
      photo: z.string().nullable(),
      /** El titular, que es el que la tabla muestra cuando hay varios. */
      default: z.boolean()
    })
  ),
  currency: z.string().nullable(),
  /**
   * El total del acuerdo de pago, **con impuesto**. Es el único monto que viaja
   * en la fila y no tiene permiso de campo: los cuatro restringidos son los del
   * bloque `*WithoutTax`, que se quedan en el detalle.
   *
   * `null` cuando la negociación todavía no tiene acuerdo de pago —el alta lo
   * permite— y por eso la pantalla que lo muestre tiene que decidir qué pinta en
   * ese caso, no asumir cero: cero es un precio, la ausencia no.
   */
  totalAmount: z.number().nullable(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema.nullable()
})

/**
 * La página del listado, con el envelope de cursor único del ADR 0019.
 *
 * Los totales y las facetas NO viajan acá: viven en
 * `GET /admin/negotiations/summary`, con los mismos filtros y sin paginar. Es la
 * decisión del ADR y tiene una consecuencia concreta para la web — el tablero
 * mantiene una pila de cursores y no preserva números de página.
 */
export const V2AdminNegotiationsPageSchema = z.object({
  data: z.array(V2AdminNegotiationRowSchema),
  pagination: V2CursorPaginationSchema
})

export type V2AdminNegotiationRow = z.infer<typeof V2AdminNegotiationRowSchema>
export type V2AdminNegotiationsPage = z.infer<typeof V2AdminNegotiationsPageSchema>

/**
 * Entrada de las transiciones de activación: inactivar y reactivar.
 *
 * Un comentario y nada más. El estado DESTINO no viaja en el wire, y esa
 * ausencia es la decisión: lo resuelve el servidor —el estado de tipo
 * `inactive` para inactivar, y el último estado previo al inactivo, leído del
 * historial, para reactivar—. Si el cliente pudiera elegirlo, «reactivar» se
 * convertiría en «mover a donde yo diga», que es otra operación y con otro
 * permiso.
 */
export const V2NegotiationActivationInputSchema = z.object({
  comment: z.string().max(500).optional()
})

/**
 * Qué devuelve una transición: la negociación y **el estado en el que quedó**.
 *
 * El estado viaja porque en la reactivación el cliente no puede saberlo de
 * antemano —lo decide el historial— y sin él la pantalla tendría que volver a
 * pedir el detalle sólo para saber dónde aterrizó lo que acaba de hacer.
 */
export const V2NegotiationActivationResultSchema = z.object({
  id: PublicIdSchemas.negotiation,
  state: z.object({
    id: PublicIdSchemas.state,
    name: z.string(),
    type: z.string()
  })
})

export type V2NegotiationActivationInput = z.infer<typeof V2NegotiationActivationInputSchema>
export type V2NegotiationActivationResult = z.infer<typeof V2NegotiationActivationResultSchema>

/**
 * Entrada del avance. `skipSAP` **sólo se honra fuera de producción**: existe
 * para poder ejercitar el golden path en `dev` sin emitir órdenes reales, y la
 * guarda de entorno del servidor es lo que impide que sea una puerta trasera
 * para saltear la facturación.
 */
export const V2AdvanceNegotiationInputSchema = V2NegotiationActivationInputSchema.extend({
  skipSAP: z.boolean().optional()
})

export type V2AdvanceNegotiationInput = z.infer<typeof V2AdvanceNegotiationInputSchema>
